import { useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import {
  Button,
  Callout,
  DataTable,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  LinkButton,
  Overline,
  ReasonField,
  Tag,
  TextField,
  apiPost,
  cx,
  dataColumns,
  errorText,
  formatDateTime,
  isApiError,
  useCommandMutation,
  useCsrf,
  useDebounced,
  useReason,
  useToast,
  type DataColumn,
} from "@amber/ui";
import {
  optionalCheckViewSchema,
  type OptionalCheckEdit,
  type OptionalCheckPreview,
  type OptionalCheckView,
  type OptionalChecksResponse,
} from "../../contracts/index";
import { evidenceKeys } from "../keys";
import { optionalCheckPreviewQuery, optionalChecksQuery } from "../queries";

const LANGUAGES = [
  ["en", "English"],
  ["de", "German"],
] as const;

function platformsText(ids: readonly string[], platforms: OptionalChecksResponse["platforms"]): string {
  if (ids.length >= platforms.length) return "All platforms";
  const labels = ids.map((id) => platforms.find((p) => p.id === id)?.label ?? id);
  return labels.length > 2 ? `${labels.slice(0, 2).join(", ")}…` : labels.join(", ");
}

function languagesText(langs: readonly string[]): string {
  const names = LANGUAGES.filter(([k]) => langs.includes(k)).map(([, l]) => l);
  return names.length === 2 ? "English and German" : (names[0] ?? "—");
}

function scopeText(c: OptionalCheckView, platforms: OptionalChecksResponse["platforms"]): string {
  return `Below ${c.scope.below.toFixed(2)} · ${platformsText(c.scope.platforms, platforms)} · ${languagesText(c.scope.languages)} · ${String(c.scope.sample_percent)}%`;
}

function usd(amount: string): string {
  const n = Number(amount);
  return n > 0 && n < 0.01 ? "<0.01" : n.toFixed(2);
}

function boundsText(c: OptionalCheckView): string {
  return `${c.performed_by === "Automated" ? `USD ${String(c.bounds.monthly_cost_usd)} a month · ` : ""}up to ${String(c.bounds.max_delay_hours)} h`;
}

const col = dataColumns<OptionalCheckView>();

function checkColumns(
  platforms: OptionalChecksResponse["platforms"],
  disabled: string | null,
  onEdit: (check: OptionalCheckView) => void,
): DataColumn<OptionalCheckView>[] {
  return [
    col.display({
      id: "check",
      header: "Check",
      meta: { cellClassName: "py-2" },
      cell: ({ row: { original: c } }) => (
        <div className="whitespace-normal">
          <span className="block font-strong text-fg">{c.name}</span>
          <span className="block text-md">
            {c.performed_by} · {c.description}
          </span>
          {c.kind === "semantic_speaker" ? (
            <span className="mt-0.5 block text-md">
              This month: {c.this_month.checked} checked · {c.this_month.skipped} skipped · USD{" "}
              {usd(c.this_month.cost_usd)}
            </span>
          ) : null}
        </div>
      ),
    }),
    col.display({
      id: "state",
      header: "State",
      meta: { width: 64 },
      cell: ({ row: { original: c } }) => (c.enabled ? <Tag tone="accent">On</Tag> : <Tag tone="quiet">Off</Tag>),
    }),
    col.display({
      id: "scope",
      header: "Scope",
      meta: { width: 190, cellClassName: "text-fg" },
      cell: ({ row: { original: c } }) =>
        c.enabled ? (
          <div className="whitespace-normal">{scopeText(c, platforms)}</div>
        ) : (
          <span className="text-muted">—</span>
        ),
    }),
    col.display({
      id: "bounds",
      header: "Bounds",
      meta: { width: 130, cellClassName: "text-fg" },
      cell: ({ row: { original: c } }) =>
        c.enabled ? <div className="whitespace-normal">{boundsText(c)}</div> : <span className="text-muted">—</span>,
    }),
    col.display({
      id: "changed",
      header: "Last changed",
      meta: { width: 130 },
      cell: ({ row: { original: c } }) =>
        c.changed ? (
          <>
            <span className="block text-fg">{c.changed.by}</span>
            <span className="block text-md">{formatDateTime(c.changed.at)}</span>
          </>
        ) : (
          "Never changed"
        ),
    }),
    col.display({
      id: "actions",
      header: () => <span className="sr-only">Actions</span>,
      meta: { label: "Actions", width: 90, cellClassName: "overflow-visible text-right" },
      cell: ({ row: { original: c } }) => (
        <Button
          size="xs"
          disabledReason={disabled}
          aria-label={`Edit ${c.name}…`}
          onClick={() => {
            onEdit(c);
          }}
        >
          <Pencil aria-hidden="true" />
          Edit…
        </Button>
      ),
    }),
  ];
}

export function OptionalChecks() {
  const query = useQuery(optionalChecksQuery);
  const [editing, setEditing] = useState<OptionalCheckView | null>(null);
  const data = query.data;
  const edit = data?.actions.edit;
  const disabled = edit && !edit.enabled ? edit.reason : null;
  return (
    <div className="mt-1 mb-4">
      <div className="mx-4 mb-1 flex items-baseline gap-2 max-compact:mx-2">
        <h2 className="m-0 text-lg font-semibold">Optional checks</h2>
        <span className="text-md text-muted">off by default</span>
      </div>
      <p className="mx-4 mt-0 mb-2 max-w-[920px] text-md text-muted max-compact:mx-2">
        Automatic assignments and scores are available without human approval. These checks add evidence on top; a
        failed optional check is recorded and never requires a person by itself. Changing a check doesn&apos;t
        re-transcribe anything.
      </p>
      {query.isError ? (
        <Callout tone="attention" role="alert" className="mx-4">
          Couldn&apos;t load the optional checks. {errorText(query.error)}
        </Callout>
      ) : null}
      <DataTable
        label="Optional checks"
        columns={checkColumns(data?.platforms ?? [], disabled ?? null, setEditing)}
        rows={data?.checks ?? []}
        rowId={(c) => c.kind}
        minWidth={780}
        isPending={query.isPending}
        skeletonRows={3}
        wrapClassName="flex-none rounded-b-xl border-b"
      />
      {editing && data ? (
        <EditOptionalCheckDialog
          check={editing}
          platforms={data.platforms}
          onClose={() => {
            setEditing(null);
          }}
        />
      ) : null}
    </div>
  );
}

interface Draft {
  enabled: boolean;
  below: string;
  platforms: string[];
  languages: ("en" | "de")[];
  sample: string;
  cost: string;
  delay: string;
}

function draftOf(c: OptionalCheckView): Draft {
  return {
    enabled: c.enabled,
    below: c.scope.below.toFixed(2),
    platforms: [...c.scope.platforms],
    languages: [...c.scope.languages],
    sample: String(c.scope.sample_percent),
    cost: String(c.bounds.monthly_cost_usd),
    delay: String(c.bounds.max_delay_hours),
  };
}

function draftErrors(
  d: Draft,
): Partial<Record<"below" | "platforms" | "languages" | "sample" | "cost" | "delay", string>> {
  const num = (v: string) => (v.trim() === "" ? Number.NaN : Number(v));
  const out: ReturnType<typeof draftErrors> = {};
  const below = num(d.below);
  if (!(below >= 0.05 && below <= 1)) out.below = "Enter a confidence from 0.05 to 1";
  if (d.platforms.length === 0) out.platforms = "Choose at least one platform";
  if (d.languages.length === 0) out.languages = "Choose at least one language";
  const sample = num(d.sample);
  if (!(Number.isInteger(sample) && sample >= 1 && sample <= 100))
    out.sample = "Enter a whole percentage from 1 to 100";
  const cost = num(d.cost);
  if (!(cost >= 0 && cost <= 100_000)) out.cost = "Enter an amount from 0";
  const delay = num(d.delay);
  if (!(Number.isInteger(delay) && delay >= 0 && delay <= 720)) out.delay = "Enter whole hours from 0 to 720";
  return out;
}

function valuesOf(d: Draft): Omit<OptionalCheckEdit, "base_revision" | "reason"> {
  return {
    enabled: d.enabled,
    scope: { below: Number(d.below), platforms: d.platforms, languages: d.languages, sample_percent: Number(d.sample) },
    bounds: { monthly_cost_usd: Number(d.cost), max_delay_hours: Number(d.delay) },
  };
}

function Section({ legend, children, hint }: { legend: string; children: ReactNode; hint?: string }) {
  return (
    <fieldset className="m-0 mt-3.5 min-w-0 border-0 p-0">
      <legend className="m-0 p-0 text-base font-semibold">{legend}</legend>
      {children}
      {hint ? <p className="m-0 mt-1.5 text-md text-muted">{hint}</p> : null}
    </fieldset>
  );
}

function Choices<T extends string>({
  label,
  options,
  value,
  onChange,
  error,
}: {
  label: string;
  options: readonly (readonly [T, string])[];
  value: readonly T[];
  onChange: (next: T[]) => void;
  error?: string | undefined;
}) {
  return (
    <div role="group" aria-label={label} className="mt-3">
      <Overline as="div" spacing="narrow" className="mb-1.5">
        {label}
      </Overline>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {options.map(([k, l]) => (
          <label key={k} className="flex min-h-6 items-center gap-1.5 text-md">
            <input
              type="checkbox"
              className="accent-[var(--color-link)]"
              checked={value.includes(k)}
              onChange={(e) => {
                onChange(e.target.checked ? [...value, k] : value.filter((v) => v !== k));
              }}
            />
            {l}
          </label>
        ))}
      </div>
      {error ? (
        <p role="alert" className="m-0 mt-1 text-md text-attention">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function RunSwitch({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={onToggle}
      className="mt-1.5 inline-flex min-h-7 items-center gap-2 rounded-md text-ui"
    >
      <span
        aria-hidden="true"
        className={cx("relative h-4 w-7 rounded-full transition-colors", enabled ? "bg-link" : "bg-surface-3")}
      >
        <span
          className={cx(
            "absolute top-0.5 size-3 rounded-full bg-bg transition-[left]",
            enabled ? "left-3.5" : "left-0.5",
          )}
        />
      </span>
      Run this check
    </button>
  );
}

interface PreviewProps {
  readonly valid: boolean;
  readonly enabled: boolean;
  readonly preview: OptionalCheckPreview | undefined;
  readonly previewError: Error | null;
}

function PreviewPanel(props: PreviewProps) {
  return (
    <div className="mt-4">
      <Overline as="h3" spacing="narrow" className="m-0 mb-1.5">
        Preview
      </Overline>
      <div aria-live="polite" className="rounded-lg border border-divider bg-surface px-3 py-2.5 text-md">
        <PreviewContent {...props} />
      </div>
    </div>
  );
}

function EditFooter({ onClose, pending, failed }: { onClose: () => void; pending: boolean; failed: boolean }) {
  return (
    <>
      <span className="mr-auto self-center text-md text-muted max-[560px]:hidden">
        Doesn&apos;t re-transcribe or block other processing
      </span>
      <DialogFooter onClose={onClose} confirm="Save" pending={pending} failed={failed} />
    </>
  );
}

function PreviewContent({ valid, enabled, preview: p, previewError }: PreviewProps) {
  if (!valid) return <p className="m-0 text-muted">Correct the highlighted values to see the preview.</p>;
  if (!enabled) return <p className="m-0">Off: no items are checked. Existing results stay recorded.</p>;
  if (p) {
    return (
      <>
        <p className="m-0">
          About{" "}
          <b className="font-strong">{p.per_week === 1 ? "1 item a week" : `${String(p.per_week)} items a week`}</b>{" "}
          {p.per_week === 1 ? "matches" : "match"} this scope
          {p.monthly_cost_usd !== null ? ` · about USD ${usd(p.monthly_cost_usd)} a month` : ""}
          {p.person_hours_per_week !== null
            ? ` · about ${String(p.person_hours_per_week)} person-hours a week added to the Tasks queue`
            : ""}
          {p.over_cap ? <span className="text-attention"> (over the cap; the rest is skipped)</span> : null}
        </p>
        <p className="m-0 mt-1 text-muted">{p.basis}</p>
      </>
    );
  }
  if (previewError) {
    return <p className="m-0 text-attention">Couldn&apos;t work out the preview. {errorText(previewError)}</p>;
  }
  return <p className="m-0 text-muted">Working out the preview…</p>;
}

function ConflictCallout({
  message,
  onReload,
  onKeep,
}: {
  message: string;
  onReload: () => Promise<void>;
  onKeep: () => Promise<void>;
}) {
  return (
    <Callout
      tone="waiting"
      role="alert"
      className="mt-3"
      title="Another Admin changed this check since you opened it"
      actions={
        <>
          <LinkButton
            onClick={() => {
              void onReload();
            }}
          >
            Use saved values
          </LinkButton>
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              void onKeep();
            }}
          >
            Keep my draft
          </Button>
        </>
      }
    >
      {message}. Your draft is kept until you choose.
    </Callout>
  );
}

function EditOptionalCheckDialog({
  check,
  platforms,
  onClose,
}: {
  check: OptionalCheckView;
  platforms: OptionalChecksResponse["platforms"];
  onClose: () => void;
}) {
  const toast = useToast();
  const client = useQueryClient();
  const csrf = useCsrf();
  const [base, setBase] = useState(check);
  const [draft, setDraft] = useState<Draft>(() => draftOf(check));
  const reason = useReason();
  const submitted = reason.submitted;
  const errors = draftErrors(draft);
  const valid = Object.keys(errors).length === 0;
  const values = valid ? valuesOf(draft) : null;
  const debounced = useDebounced(values, 250);
  const preview = useQuery(optionalCheckPreviewQuery(check.kind, debounced, base.revision, csrf));
  const mutation = useCommandMutation({
    run: (token) =>
      values
        ? apiPost(
            `/api/evidence/optional-checks/${check.kind}`,
            { ...values, base_revision: base.revision, reason: reason.trimmed },
            optionalCheckViewSchema,
            token,
          )
        : Promise.reject(new Error("Correct the highlighted values")),
    invalidate: [evidenceKeys.optionalChecks],
    onSuccess: (saved) => {
      toast.show(`${saved.name} saved · ${saved.enabled ? "on" : "off"}`);
      onClose();
    },
  });
  const conflict = isApiError(mutation.error) && mutation.error.code === "conflict" ? mutation.error : null;
  const fetchCurrent = async (): Promise<OptionalCheckView | null> => {
    const fresh = await client.query({ ...optionalChecksQuery, staleTime: 0 });
    return fresh.checks.find((c) => c.kind === check.kind) ?? null;
  };
  const keepDraft = async () => {
    const current = await fetchCurrent();
    if (current) setBase(current);
    mutation.reset();
  };
  const reload = async () => {
    const current = await fetchCurrent();
    if (current) {
      setBase(current);
      setDraft(draftOf(current));
    }
    mutation.reset();
  };
  const shown = (k: keyof ReturnType<typeof draftErrors>) => (submitted ? errors[k] : undefined);
  const field = (k: "below" | "sample" | "cost" | "delay", label: string, hint?: string) => (
    <TextField
      label={label}
      inputMode="decimal"
      autoComplete="off"
      value={draft[k]}
      error={submitted || draft[k] !== draftOf(base)[k] ? (errors[k] ?? null) : null}
      {...(hint ? { hint } : {})}
      onChange={(e) => {
        setDraft({ ...draft, [k]: e.target.value });
      }}
    />
  );
  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={check.name}
      onSubmit={() => {
        if (!reason.submit() || !valid || conflict) return;
        mutation.mutate();
      }}
      footer={<EditFooter onClose={onClose} pending={mutation.isPending} failed={mutation.isError && !conflict} />}
    >
      <DialogText>
        {check.performed_by} check · {base.enabled ? "on" : "off"} now. {check.description}
      </DialogText>
      <Section legend="State">
        <RunSwitch
          enabled={draft.enabled}
          onToggle={() => {
            setDraft({ ...draft, enabled: !draft.enabled });
          }}
        />
      </Section>
      <Section legend="Scope">
        <div className="grid grid-cols-2 gap-x-3 max-[560px]:grid-cols-1">
          {field("below", "Attribution confidence below")}
          {field("sample", "Sample (% of matching items)")}
        </div>
        <Choices
          label="Platforms"
          options={platforms.map((p) => [p.id, p.label] as const)}
          value={draft.platforms}
          error={shown("platforms")}
          onChange={(next) => {
            setDraft({ ...draft, platforms: next });
          }}
        />
        <Choices
          label="Languages"
          options={LANGUAGES}
          value={draft.languages}
          error={shown("languages")}
          onChange={(next) => {
            setDraft({ ...draft, languages: next });
          }}
        />
      </Section>
      <Section
        legend="Bounds"
        hint="When a bound is reached, items continue without the check and the skipped check is recorded."
      >
        <div className="grid grid-cols-2 gap-x-3 max-[560px]:grid-cols-1">
          {field("cost", "Cost cap per month (USD)")}
          {field("delay", "Longest added delay (hours)")}
        </div>
      </Section>
      <PreviewPanel
        valid={valid}
        enabled={draft.enabled}
        preview={preview.data}
        previewError={preview.isError ? preview.error : null}
      />
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      {conflict ? (
        <ConflictCallout message={conflict.message} onReload={reload} onKeep={keepDraft} />
      ) : (
        <FailedCallout error={mutation.error} />
      )}
    </Dialog>
  );
}
